import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layers, ExternalLink, Loader2, Sparkles, Tag } from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ToastContainer from '../../components/ui/ToastContainer';
import { useToast } from '../../hooks/useToast';
import { settingsService } from '../../services/api/settings';
import { formatUseCaseCategoryLabel } from '../../utils/useCaseCategories';
import { ouvrirUseCase } from '../../utils/openUseCase';

interface UseCase {
  id: number;
  title: string;
  description: string;
  /** Phrase courte affichée sur la vignette ; la description complète sert à l'orientation. */
  summary?: string;
  category?: string;
  category_code?: string;
  category_name?: string;
  link?: string;
  mode?: 'lien' | 'natif';
  image?: string;
  image_url?: string;
  is_active?: boolean;
  order?: number;
}

const USECASE_CONTEXT = 'brainstorming';

const UseCasesPage: React.FC = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, showError, showInfo } = useToast();
  const [useCases, setUseCases] = useState<UseCase[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [useCasesData, categoriesData] = await Promise.all([
          settingsService.getUseCases({ context: USECASE_CONTEXT, is_active: true }),
          settingsService.getUseCaseCategories({ context: USECASE_CONTEXT, is_active: true }),
        ]);
        setUseCases(useCasesData);
        setCategories(
          Array.isArray(categoriesData)
            ? categoriesData.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            : []
        );
      } catch (error) {
        console.error('❌ Erreur chargement use cases (UseCasesPage):', error);
        showError('Erreur de chargement', 'Impossible de récupérer les use cases.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showError]);

  const groupedUseCases = useMemo(() => {
    const groups: Record<string, UseCase[]> = {};

    useCases.forEach((useCase) => {
      const key = useCase.category_code || useCase.category || 'non_classe';
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(useCase);
    });

    // Ordonner chaque groupe par ordre éventuel ou titre
    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return a.title.localeCompare(b.title);
      });
    });

    return groups;
  }, [useCases]);

  const categoryChips = useMemo(() => {
    const chips = [
      {
        value: 'all',
        label: `Tous (${useCases.length})`,
        count: useCases.length,
      },
    ];

    categories.forEach((category: any) => {
      chips.push({
        value: category.code,
        label: `${category.name} (${groupedUseCases[category.code]?.length ?? 0})`,
        count: groupedUseCases[category.code]?.length ?? 0,
      });
    });

    return chips;
  }, [categories, groupedUseCases, useCases.length]);

  const displayedUseCases = useMemo(() => {
    if (activeCategory === 'all') {
      return [...useCases].sort((a, b) => a.title.localeCompare(b.title));
    }
    return groupedUseCases[activeCategory] ?? [];
  }, [activeCategory, groupedUseCases, useCases]);

  const handleOpenLink = (useCase: UseCase) => {
    if (!ouvrirUseCase(useCase, navigate)) {
      showInfo('Lien indisponible', 'Ce use case ne possède pas encore de lien associé.');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <div className="flex items-center space-x-3 text-eg-accent">
              <Layers className="w-6 h-6" />
              <span className="text-sm font-semibold uppercase tracking-wide">
                Bibliothèque de Use Cases
              </span>
            </div>
            <h1 className="mt-2 text-3xl font-bold text-eg-encre">
              Inspirez-vous de nos scénarios d&apos;utilisation
            </h1>
            <p className="mt-3 text-eg-gris max-w-2xl">
              Explorez toutes les façons dont omnia-eg peut booster vos processus. 
            </p>
          </div>
          <div className="flex items-center space-x-3 bg-teal-light/10 border border-teal-light/40 px-4 py-3 rounded-md text-eg-accent">
            <Sparkles className="w-5 h-5" />
            <div className="text-sm leading-tight">
              <p className="font-semibold">Catégories Use Cases</p>
              <p>Selectionner vos propres categories de use cases et decouvrez-les.</p>
            </div>
          </div>
        </div>

        <Card className="p-4">
          <div className="flex flex-wrap gap-2">
            {categoryChips.map((category) => {
              const isActive = activeCategory === category.value;
              return (
                <button
                  key={category.value}
                  onClick={() => setActiveCategory(category.value)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    isActive
                      ? 'bg-eg-ombre text-white '
                      : 'bg-eg-voile text-eg-brun hover:bg-gray-200'
                  }`}
                >
                  {category.label}
                  {category.value !== 'all' && (
                    <span className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-eg-blanc/30 text-xs font-semibold text-white">
                      {category.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </Card>

        {loading ? (
          <Card className="py-20">
            <div className="flex flex-col items-center justify-center space-y-4 text-eg-gris">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p>Chargement des use cases en cours...</p>
            </div>
          </Card>
        ) : displayedUseCases.length === 0 ? (
          <Card className="py-20">
            <div className="flex flex-col items-center justify-center space-y-4 text-eg-gris">
              <Layers className="w-10 h-10" />
              <p className="text-lg font-medium">Aucun use case dans cette catégorie.</p>
              <p className="text-sm text-eg-estompe">
                Aucun use case disponible pour le moment.
              </p>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {displayedUseCases.map((useCase) => {
              const imageSource = useCase.image_url || useCase.image;
              const categoryLabel =
                useCase.category_name ||
                formatUseCaseCategoryLabel(useCase.category_code || useCase.category || '');

              return (
                <motion.div
                  key={useCase.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => handleOpenLink(useCase)}
                  className="cursor-pointer"
                >
                  <Card className="h-full flex flex-col overflow-hidden border border-eg-bord">
                    {imageSource && (
                      <div className="relative">
                        <img
                          src={imageSource}
                          alt={useCase.title}
                          className="w-full h-40 object-cover"
                        />
                        <div className="absolute top-3 left-3 bg-eg-blanc/80 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold text-eg-encre flex items-center space-x-1">
                          <Tag className="w-3 h-3" />
                          <span>{categoryLabel}</span>
                        </div>
                      </div>
                    )}

                    <div className="flex-1 flex flex-col p-5 space-y-4">
                      {!imageSource && (
                        <span className="inline-flex items-center self-start rounded-full bg-teal-light/15 px-2.5 py-1 text-xs font-semibold text-eg-accent">
                          {categoryLabel}
                        </span>
                      )}
                      <div>
                        <h3 className="text-lg font-semibold text-eg-encre">
                          {useCase.title}
                        </h3>
                        <p className="mt-2 text-sm text-eg-gris line-clamp-3">
                          {useCase.summary || useCase.description}
                        </p>
                      </div>
                      <div className="mt-auto flex items-center justify-between pt-3 border-t border-eg-bordure">
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded-full ${
                            useCase.is_active
                              ? 'bg-eg-valide/10 text-green-700'
                              : 'bg-gray-200 text-eg-gris'
                          }`}
                        >
                          {useCase.is_active ? 'Actif' : 'Inactif'}
                        </span>
                        <Button
                          variant={useCase.mode === 'natif' ? 'primary' : 'outline'}
                          size="sm"
                          icon={
                            useCase.mode === 'natif'
                              ? <Sparkles className="w-4 h-4" />
                              : <ExternalLink className="w-4 h-4" />
                          }
                        >
                          Ouvrir
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </DashboardLayout>
  );
};

export default UseCasesPage;
